import * as React from "react";
import {
    ActionButton,
    Button,
    Card,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    Tooltip,
} from "@jhool-io/fe-components";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
    cn,
    formatDate,
    removeEnumUnderscore,
    makeStringFirstLetterCapital,
    displayNameInRightFormat,
} from "../../../../../utils/helpers";
import {
    ClientFeeValues,
    IClientFee,
    IClientFeeValue,
} from "../../../types/clients.types";
import {
    getFeesToHighlightFromFee,
    getProviderApplicableFeesFromFee,
} from "../../../helpers/clients.helpers";
import { useFetchPracticeSettings } from "../../../../practice-settings/hooks/queries/practice-settings";
import { useEditClientCurrentFee } from "../../../hooks/clients.mutations";
import useToast from "../../../../../hooks/useToast";
import { IPaginatedApiResponse } from "../../../../../utils/types/api-response";
import {
    calculateFees,
    getMessageForFees,
} from "../../../../../utils/helpers/fees/fees";
import useShowInsuranceOverSelfPay from "../../../../practice-settings/hooks/useShowInsuranceOverSelfPay";
import InfoIcon from "../../../../../icons/Info";
import ChevronDownIcon from "../../../../../icons/ChevronDown";
import ChevronUpIcon from "../../../../../icons/ChevronUp";

interface IClientSingleFeeProps {
    fee: IClientFee;
    defaultIsMaximized: boolean;
    isCurrentFee: boolean;
}

export default function ClientSingleFee({
    fee,
    defaultIsMaximized,
    isCurrentFee,
}: IClientSingleFeeProps) {
    const [isMaximized, setIsMaximized] = React.useState(defaultIsMaximized);

    const { clientId } = useParams();

    const { data: practiceFeeSettings } = useFetchPracticeSettings({
        setting_name: "fee_type",
        category: "fees",
    });

    const editFee = useEditClientCurrentFee(clientId || "", fee.fee_id);

    const showInsOverSelfPay = useShowInsuranceOverSelfPay();

    const queryClient = useQueryClient();

    const { toast } = useToast();

    const practiceHasINNFee =
        practiceFeeSettings &&
        practiceFeeSettings?.data.settings.length > 0 &&
        practiceFeeSettings?.data.settings[0].current_value === "INN";

    const desiredFeeValuesOrder = React.useMemo(() => {
        const baseFees = [
            "drs fee",
            "lic_fee",
            "masters fee",
            "intern fee",
            "psych fee",
            "dr_fee_a",
            "dr_fee_b",
            "dr_fee_c",
            "lic_a_fee",
            "lic_b_fee",
            "lic_c_fee",
            "testing fee",
            "gifted assessment fee",
            "short term evaluation documentation fee",
        ];

        return practiceHasINNFee
            ? [...baseFees, ClientFeeValues.INN_MASTERS_FEE]
            : baseFees;
    }, [practiceHasINNFee]);

    const orderMap = React.useMemo(() => {
        const map: { [key: string]: number } = {};
        desiredFeeValuesOrder.forEach((type, index) => {
            map[type] = index;
        });
        return map;
    }, [desiredFeeValuesOrder]);

    const getFeeValuesInDesiredFormat = (fees: IClientFeeValue[]) => {
        return fees.sort((a, b) => {
            const aOrder = orderMap[a.type] ?? 999;
            const bOrder = orderMap[b.type] ?? 999;
            return aOrder - bOrder;
        });
    };

    const { defaultFeeValues, providerFeeValues } = React.useMemo(() => {
        const defaults: IClientFeeValue[] = [];
        const providerSpecific: IClientFeeValue[] = [];

        fee.fee_values.forEach((f) => {
            if (f.provider_id || f.provider?.provider_id) {
                providerSpecific.push(f);
            } else {
                defaults.push(f);
            }
        });

        return {
            defaultFeeValues: defaults,
            providerFeeValues: providerSpecific,
        };
    }, [fee.fee_values]);

    const feeValuesInDesiredFormat =
        getFeeValuesInDesiredFormat(defaultFeeValues);

    const providerApplicableFees = React.useMemo(
        () => getProviderApplicableFeesFromFee(fee),
        [fee]
    );

    const feesToHighlight = React.useMemo(
        () => getFeesToHighlightFromFee(fee),
        [fee]
    );

    const providerFeesByProvider = React.useMemo(() => {
        const grouped = new Map<
            string,
            {
                provider: {
                    provider_id: string;
                    first_name: string;
                    last_name: string;
                };
                fees: {
                    type: ClientFeeValues;
                    value: string;
                    isApplicable: boolean;
                }[];
            }
        >();

        providerFeeValues.forEach((fv) => {
            if (!fv.provider) return;

            // Check if this provider fee is an applicable fee
            const isApplicable = providerApplicableFees.some(
                (p) =>
                    p.provider_id === fv.provider?.provider_id &&
                    p.applicable_fee_type === fv.type
            );

            const existing = grouped.get(fv.provider.provider_id);
            if (existing) {
                existing.fees.push({
                    type: fv.type,
                    value: fv.value,
                    isApplicable,
                });
            } else {
                grouped.set(fv.provider.provider_id, {
                    provider: fv.provider,
                    fees: [{ type: fv.type, value: fv.value, isApplicable }],
                });
            }
        });

        grouped.forEach((entry) => {
            entry.fees.sort(
                (a, b) => (orderMap[a.type] ?? 999) - (orderMap[b.type] ?? 999)
            );
        });

        return [...grouped.values()];
    }, [providerFeeValues, providerApplicableFees, orderMap]);

    const { topDesiredValues, bottomDesiredValues } = React.useMemo(() => {
        const desiredFeesOrderTop = desiredFeeValuesOrder.slice(0, 5);

        const applicableFeeSet = new Set(feesToHighlight);
        const topOrderSet = new Set(desiredFeesOrderTop);

        const categorizedFees: {
            applicableTop: IClientFeeValue[];
            applicableBottom: IClientFeeValue[];
            nonApplicableTop: IClientFeeValue[];
            nonApplicableBottom: IClientFeeValue[];
        } = {
            applicableTop: [],
            applicableBottom: [],
            nonApplicableTop: [],
            nonApplicableBottom: [],
        };

        feeValuesInDesiredFormat.forEach((value) => {
            const isApplicable = applicableFeeSet.has(value.type);
            const isTop = topOrderSet.has(value.type);
            const isINNMastersFee =
                value.type === ClientFeeValues.INN_MASTERS_FEE;

            if (isINNMastersFee && !isApplicable && !practiceHasINNFee) {
                return;
            }

            if (isApplicable && isTop) {
                categorizedFees.applicableTop.push(value);
            } else if (isApplicable && !isTop) {
                categorizedFees.applicableBottom.push(value);
            } else if (!isApplicable && isTop) {
                categorizedFees.nonApplicableTop.push(value);
            } else {
                categorizedFees.nonApplicableBottom.push(value);
            }
        });

        const topValues = [
            ...categorizedFees.applicableTop,
            ...categorizedFees.applicableBottom,
            ...categorizedFees.nonApplicableTop,
            ...categorizedFees.nonApplicableBottom,
        ].slice(0, 5);

        const topValueIds = new Set(topValues.map((v) => v.fee_value_id));
        const bottomValues = [
            ...categorizedFees.applicableTop,
            ...categorizedFees.applicableBottom,
            ...categorizedFees.nonApplicableTop,
            ...categorizedFees.nonApplicableBottom,
        ].filter((value) => !topValueIds.has(value.fee_value_id));

        return {
            topDesiredValues: topValues,
            bottomDesiredValues: bottomValues,
        };
    }, [
        feeValuesInDesiredFormat,
        desiredFeeValuesOrder,
        feesToHighlight,
        practiceHasINNFee,
    ]);

    const handleEditClientFee = (payload: {
        self_pay?: boolean;
        accepts_assignment?: boolean;
        alt_email_sent?: boolean;
    }) => {
        const payloadToSend = {
            ...fee,
            reason_for_update: "",
            self_pay: payload.self_pay ?? fee.self_pay,
            accepts_assignment:
                payload.accepts_assignment ?? fee.accepts_assignment,
            alt_email_sent: payload.alt_email_sent ?? fee.alt_email_sent,
        };

        editFee.mutate(payloadToSend, {
            onSuccess: (res) => {
                queryClient.setQueriesData<IPaginatedApiResponse<IClientFee[]>>(
                    [clientId, `fees`, 200],
                    (prev) => {
                        const prevFees = prev as IPaginatedApiResponse<
                            IClientFee[]
                        >;
                        return {
                            ...prevFees,
                            data: prevFees?.data.map((singleFee) => {
                                if (fee.fee_id === singleFee.fee_id) {
                                    return {
                                        ...singleFee,
                                        self_pay:
                                            payload.self_pay ?? fee.self_pay,
                                        accepts_assignment:
                                            payload.accepts_assignment ??
                                            fee.accepts_assignment,
                                        alt_email_sent:
                                            payload.alt_email_sent ??
                                            fee.alt_email_sent,
                                    };
                                }
                                return singleFee;
                            }),
                        };
                    }
                );

                queryClient.invalidateQueries({
                    queryKey: [clientId, "fees"],
                });
                queryClient.invalidateQueries({
                    queryKey: [clientId, "insurance-fees"],
                });

                toast({
                    mode: "success",
                    message: res.message || "Fee updated successfully",
                });
            },
            onError: (error) => {
                toast({
                    mode: "error",
                    message:
                        error.response?.data.message ||
                        "Could not update fee at this time",
                });
            },
        });
    };

    let updatedBy = "Intake";

    if (fee?.updated_by_intake_user) {
        updatedBy = fee?.updated_by_intake_user;
    } else if (fee?.updated_by) {
        updatedBy = displayNameInRightFormat({
            firstName: makeStringFirstLetterCapital(
                fee?.updated_by?.first_name
            ),
            lastName: makeStringFirstLetterCapital(
                fee.updated_by?.last_name || ""
            ),
        });
    }

    return (
        <Card classNames="h-auto min-w-[1000px] max-w-full overflow-x-auto p-0">
            <div>
                <div className="px-[--padding-card-x-lg] py-[--padding-card-y-lg] flex items-center justify-between gap-x-[--spacing-items]">
                    <div className="flex items-center gap-x-[--spacing-items]">
                        <div className="flex items-center justify-center rounded-md shrink-0 h-[43px] w-[47px] bg-[--card-bg-gray]">
                            <svg
                                viewBox="0 0 24 24"
                                width="24"
                                height="24"
                                stroke="currentColor"
                                strokeWidth="2"
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="size-5 shrink-0 stroke-text opacity-70"
                            >
                                <rect
                                    x="1"
                                    y="4"
                                    width="22"
                                    height="16"
                                    rx="2"
                                    ry="2"
                                />
                                <line x1="1" y1="10" x2="23" y2="10" />
                            </svg>
                        </div>
                        <div className="flex flex-col justify-center gap-y-1 text-caption">
                            <span className="font-semibold">
                                {fee.updated_at
                                    ? formatDate(fee?.updated_at)
                                    : "--"}
                            </span>
                            <span className="text-textSec font-regular">
                                {`Updated by ${updatedBy}`}
                            </span>
                        </div>
                    </div>
                    <div className="flex flex-col justify-center gap-y-1 text-caption">
                        <span className="uppercase font-semibold">
                            START DATE
                        </span>
                        <span className="text-textSec font-regular">
                            {fee.start_date ? formatDate(fee.start_date) : "--"}
                        </span>
                    </div>
                    <div className="flex flex-col justify-center gap-y-1 text-caption">
                        <span className="uppercase font-semibold">
                            END DATE
                        </span>
                        <span className="text-textSec font-regular">
                            {fee.end_date ? formatDate(fee.end_date) : "--"}
                        </span>
                    </div>
                    <div className="flex items-center">
                        <div className="flex items-center gap-x-2">
                            {topDesiredValues.map((item) => {
                                const feesCalculated = calculateFees(
                                    item.value
                                );

                                const hideToolTip = Boolean(
                                    feesCalculated &&
                                        feesCalculated.ongoingFee &&
                                        !feesCalculated.isTbd &&
                                        !feesCalculated.evalFee &&
                                        !feesCalculated.udmFee
                                );

                                const isApplicableFee =
                                    feesToHighlight.includes(item.type);

                                return hideToolTip ? (
                                    <div
                                        className={cn(
                                            "flex px-2 py-1 flex-col justify-center gap-y-[5px] w-[125px] rounded-md",
                                            {
                                                "[border:1px_solid_var(--color-primary)]  bg-[#eefdfda3]":
                                                    isApplicableFee &&
                                                    item.value,
                                            }
                                        )}
                                        key={item.type}
                                    >
                                        <span
                                            className={cn(
                                                "uppercase font-semibold text-caption",
                                                {
                                                    "after:content-['*'] after:text-caption after:font-semibold":
                                                        isApplicableFee &&
                                                        item.value,
                                                }
                                            )}
                                        >
                                            {removeEnumUnderscore(item.type)}
                                        </span>
                                        <span className="text-textSec text-caption font-regular">
                                            {item.value || "--"}
                                        </span>
                                    </div>
                                ) : (
                                    <div
                                        className={cn(
                                            "flex px-2 py-1 flex-col justify-center gap-y-[5px] w-[125px] rounded-md",
                                            {
                                                "[border:1px_solid_var(--color-primary)]  bg-[#eefdfda3]":
                                                    isApplicableFee &&
                                                    item.value,
                                            }
                                        )}
                                    >
                                        <span
                                            className={cn(
                                                "uppercase font-semibold text-caption",
                                                {
                                                    "after:content-['*'] after:text-caption after:font-semibold":
                                                        isApplicableFee &&
                                                        item.value,
                                                }
                                            )}
                                        >
                                            {removeEnumUnderscore(item.type)}
                                        </span>
                                        <span className="text-textSec font-regular flex items-center gap-x-1">
                                            {item.value || "--"}
                                            {item.value ? (
                                                <ActionButton
                                                    variant="normal"
                                                    size="auto"
                                                    actionTitle={getMessageForFees(
                                                        item.value
                                                    )}
                                                >
                                                    <InfoIcon className="stroke-textSec" />
                                                </ActionButton>
                                            ) : (
                                                ""
                                            )}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="ml-3">
                            <Button
                                variant="secondary"
                                className="bg-[--card-bg-gray]"
                                onClick={() =>
                                    setIsMaximized((state) => !state)
                                }
                            >
                                {isMaximized ? (
                                    <ChevronUpIcon className="transition-all ease-out duration-200 stroke-text" />
                                ) : (
                                    <ChevronDownIcon className="transition-all ease-out duration-200 stroke-text" />
                                )}
                            </Button>
                        </div>
                    </div>
                </div>

                {providerFeesByProvider.length > 0 ? (
                    <div className="px-[--padding-card-x-lg]">
                        <div className="text-xs font-semibold text-textSec uppercase py-1">
                            Therapist-specific fees
                        </div>
                        <div className="flex items-center gap-x-2 mb-[--spacing-items]">
                            {providerFeesByProvider.map(
                                ({ provider, fees }) => (
                                    <div
                                        key={provider.provider_id}
                                        className="flex items-center gap-x-1 bg-[#eefdfda3] [border:1px_solid_var(--color-primary)] p-1 rounded-md"
                                    >
                                        <span className="text-xs text-textSec">
                                            {provider.first_name}{" "}
                                            {provider.last_name}:
                                        </span>
                                        <span className="flex items-center gap-x-6 text-xs">
                                            {fees.map((providerFee) => (
                                                <span
                                                    key={providerFee.type}
                                                    className="flex items-center gap-x-1"
                                                >
                                                    <span className="uppercase">
                                                        {removeEnumUnderscore(
                                                            providerFee.type
                                                        )}
                                                    </span>{" "}
                                                    <span className="font-semibold">
                                                        {providerFee.value}
                                                    </span>
                                                </span>
                                            ))}
                                        </span>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                ) : null}
            </div>
            <div
                className={cn(
                    "h-0 overflow-y-hidden transition-all ease-out duration-200",
                    {
                        "h-auto": isMaximized,
                    }
                )}
            >
                <div className="grid grid-cols-9 gap-x-2 px-[--padding-card-x-lg] py-[--padding-card-y-lg] bg-[--card-bg-gray] [border-top:1px_solid_var(--color-stroke)] [border-bottom:1px_solid_var(--color-stroke)] font-semibold text-caption text-textSec uppercase">
                    <span>THERAPIST</span>
                    <span>SESSION TYPE</span>
                    <span>START DATE</span>
                    <span>FEE</span>
                    <span>SESSION COUNT</span>
                    <span>--</span>
                    <span>--</span>
                    <span>--</span>
                    <span>--</span>
                </div>
                <div className="px-[--padding-card-x-lg] py-[--padding-card-y-lg] bg-[--card-bg] flex flex-col gap-y-[--spacing-items] text-base font-regular">
                    {fee.providers.map((provider) => {
                        return (
                            <div
                                className="grid grid-cols-9 gap-x-2 w-full"
                                key={provider.provider_id}
                            >
                                <span>
                                    {displayNameInRightFormat({
                                        firstName: provider.first_name,
                                        lastName: provider.last_name,
                                    })}
                                </span>
                                <span className="capitalize">
                                    {removeEnumUnderscore(provider.service)}
                                </span>
                                <span>
                                    {provider.start_date
                                        ? formatDate(provider.start_date, true)
                                        : null}
                                </span>
                                <span>{provider.applicable_fee_value}</span>
                                <span>{provider.session_count ?? 0}</span>
                                <span>--</span>
                                <span>--</span>
                                <span>--</span>
                                <span>--</span>
                            </div>
                        );
                    })}
                </div>
                <div className="grid grid-cols-9 gap-x-2 px-[--padding-card-x-lg] py-[--padding-card-y-lg] bg-[--card-bg-gray] [border-top:1px_solid_var(--color-stroke)] [border-bottom:1px_solid_var(--color-stroke)] font-semibold text-caption text-textSec uppercase">
                    {bottomDesiredValues.map((item) => (
                        <span key={item.type}>
                            {removeEnumUnderscore(item.type)}
                        </span>
                    ))}
                </div>
                <div className="px-[--padding-card-x-lg] py-[--padding-card-y-lg]">
                    <div className="grid grid-cols-9 gap-x-2 w-full text-base font-regular items-center">
                        {bottomDesiredValues.map((item) => {
                            const feesCalculated = calculateFees(item.value);

                            const hideToolTip = Boolean(
                                feesCalculated &&
                                    feesCalculated.ongoingFee &&
                                    !feesCalculated.isTbd &&
                                    !feesCalculated.evalFee &&
                                    !feesCalculated.udmFee
                            );

                            const isApplicableFee = feesToHighlight.includes(
                                item.type
                            );

                            return hideToolTip ? (
                                <div>
                                    <span
                                        className={cn(
                                            "rounded-md inline-block",
                                            {
                                                "[border:1px_solid_var(--color-primary)]  bg-[#eefdfda3] p-2":
                                                    isApplicableFee &&
                                                    item.value,
                                            }
                                        )}
                                    >
                                        {item.value || "--"}
                                    </span>
                                </div>
                            ) : (
                                <div>
                                    <span
                                        className={cn(
                                            "rounded-md inline-flex items-center gap-x-1",
                                            {
                                                "[border:1px_solid_var(--color-primary)]  bg-[#eefdfda3] p-2":
                                                    isApplicableFee &&
                                                    item.value,
                                            }
                                        )}
                                    >
                                        {item.value || "--"}
                                        {item.value ? (
                                            <Tooltip
                                                content={getMessageForFees(
                                                    item.value
                                                )}
                                            >
                                                <Button
                                                    variant="normal"
                                                    size="auto"
                                                >
                                                    <InfoIcon />
                                                </Button>
                                            </Tooltip>
                                        ) : null}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div className="grid grid-cols-9 gap-x-2 px-[--padding-card-x-lg] py-[--padding-card-y-lg] bg-[--card-bg-gray] [border-top:1px_solid_var(--color-stroke)] [border-bottom:1px_solid_var(--color-stroke)] font-semibold text-caption text-textSec uppercase">
                    <span>oonded</span>
                    <span>oonco</span>
                    <span>oonoop</span>
                    <span>ooncoins</span>
                    <span>innded</span>
                    <span>innco</span>
                    <span>INNOOP</span>
                    <span>INNCOINS</span>
                    <span>UCR</span>
                </div>
                <div className="px-[--padding-card-x-lg] py-[--padding-card-y-lg]">
                    <div className="grid grid-cols-9 gap-x-2 w-full text-caption font-regular items-center lowercase">
                        <span>{fee.oonded || "n/a"}</span>
                        <span>{fee.oonco || "n/a"}</span>
                        <span>{fee.oonoop || "n/a"}</span>
                        <span>{fee.ooncoins || "n/a"}</span>
                        <span>{fee.innded || "n/a"}</span>
                        <span>{fee.innco || "n/a"}</span>
                        <span>{fee.innoop || "n/a"}</span>
                        <span>{fee.inncoins || "n/a"}</span>
                        <span>{fee.ucr || "n/a"}</span>
                    </div>
                </div>
                <div className="grid grid-cols-9 gap-x-2 px-[--padding-card-x-lg] py-[--padding-card-y-lg] bg-[--card-bg-gray] [border-top:1px_solid_var(--color-stroke)] [border-bottom:1px_solid_var(--color-stroke)] font-semibold text-caption text-textSec uppercase">
                    {!showInsOverSelfPay ? <span>SELFPAY</span> : null}
                    <span>ACCEPT ASSIG.</span>
                    {!showInsOverSelfPay ? <span>ALT EMAIL SENT</span> : null}
                    <span className="col-span-6">REASON FOR UPDATE</span>
                </div>
                <div className=" px-[--padding-card-x-lg] py-[--padding-card-y-lg]">
                    <div className="grid grid-cols-9 gap-x-2 w-full text-caption font-regular items-center">
                        {!showInsOverSelfPay ? (
                            <div>
                                {isCurrentFee ? (
                                    <Dropdown>
                                        <DropdownTrigger asChild>
                                            <FilterButton
                                                className={cn(
                                                    "px-2 ![border:none] rounded-md",
                                                    {
                                                        "text-[#00563E] bg-[rgba(204,250,233,0.50)] hover:bg-[rgba(204,250,233,0.50)] hover:brightness-90":
                                                            fee.self_pay,
                                                        "text-[#981F41]  bg-[rgba(251,199,198,0.50)] hover:bg-[rgba(251,199,198,0.50)] hover:brightness-90":
                                                            !fee.self_pay,
                                                    }
                                                )}
                                            >
                                                {fee.self_pay ? "Yes" : "No"}
                                            </FilterButton>
                                        </DropdownTrigger>
                                        <DropdownContent>
                                            <DropdownItem
                                                onClick={() =>
                                                    handleEditClientFee({
                                                        self_pay: true,
                                                    })
                                                }
                                            >
                                                Yes
                                            </DropdownItem>
                                            <DropdownItem
                                                onClick={() =>
                                                    handleEditClientFee({
                                                        self_pay: false,
                                                    })
                                                }
                                            >
                                                No
                                            </DropdownItem>
                                        </DropdownContent>
                                    </Dropdown>
                                ) : (
                                    <span
                                        className={cn(
                                            "px-2 rounded-md h-7 inline-flex items-center justify-center",
                                            {
                                                "text-[#00563E] bg-[rgba(204,250,233,0.50)] hover:bg-[rgba(204,250,233,0.50)] ":
                                                    fee.self_pay,
                                                "text-[#981F41]  bg-[rgba(251,199,198,0.50)] hover:bg-[rgba(251,199,198,0.50)] ":
                                                    !fee.self_pay,
                                            }
                                        )}
                                    >
                                        {fee.self_pay ? "Yes" : "No"}
                                    </span>
                                )}
                            </div>
                        ) : null}
                        {isCurrentFee ? (
                            <div>
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton
                                            className={cn(
                                                "px-2 ![border:none] rounded-md",
                                                {
                                                    "text-[#00563E] bg-[rgba(204,250,233,0.50)] hover:bg-[rgba(204,250,233,0.50)] hover:brightness-90":
                                                        fee.accepts_assignment,
                                                    "text-[#981F41]  bg-[rgba(251,199,198,0.50)] hover:bg-[rgba(251,199,198,0.50)] hover:brightness-90":
                                                        !fee.accepts_assignment,
                                                }
                                            )}
                                        >
                                            {fee.accepts_assignment
                                                ? "Yes"
                                                : "No"}
                                        </FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent>
                                        <DropdownItem
                                            onClick={() =>
                                                handleEditClientFee({
                                                    accepts_assignment: true,
                                                })
                                            }
                                        >
                                            Yes
                                        </DropdownItem>
                                        <DropdownItem
                                            onClick={() =>
                                                handleEditClientFee({
                                                    accepts_assignment: false,
                                                })
                                            }
                                        >
                                            No
                                        </DropdownItem>
                                    </DropdownContent>
                                </Dropdown>
                            </div>
                        ) : (
                            <div>
                                <span
                                    className={cn(
                                        "px-2 rounded-md h-7 inline-flex items-center justify-center",
                                        {
                                            "text-[#00563E] bg-[rgba(204,250,233,0.50)] hover:bg-[rgba(204,250,233,0.50)] ":
                                                fee.accepts_assignment,
                                            "text-[#981F41]  bg-[rgba(251,199,198,0.50)] hover:bg-[rgba(251,199,198,0.50)] ":
                                                !fee.accepts_assignment,
                                        }
                                    )}
                                >
                                    {fee.accepts_assignment ? "Yes" : "No"}
                                </span>
                            </div>
                        )}
                        {!showInsOverSelfPay ? (
                            <div>
                                {isCurrentFee ? (
                                    <Dropdown>
                                        <DropdownTrigger asChild>
                                            <FilterButton
                                                className={cn(
                                                    "px-2 ![border:none]",
                                                    {
                                                        "text-[#00563E] bg-[rgba(204,250,233,0.50)] hover:bg-[rgba(204,250,233,0.50)] hover:brightness-90":
                                                            fee.alt_email_sent,
                                                        "text-[#981F41]  bg-[rgba(251,199,198,0.50)] hover:bg-[rgba(251,199,198,0.50)] hover:brightness-90":
                                                            !fee.alt_email_sent,
                                                    }
                                                )}
                                            >
                                                {fee.alt_email_sent
                                                    ? "Yes"
                                                    : "No"}
                                            </FilterButton>
                                        </DropdownTrigger>
                                        <DropdownContent>
                                            <DropdownItem
                                                onClick={() =>
                                                    handleEditClientFee({
                                                        alt_email_sent: true,
                                                    })
                                                }
                                            >
                                                Yes
                                            </DropdownItem>
                                            <DropdownItem
                                                onClick={() =>
                                                    handleEditClientFee({
                                                        alt_email_sent: false,
                                                    })
                                                }
                                            >
                                                No
                                            </DropdownItem>
                                        </DropdownContent>
                                    </Dropdown>
                                ) : (
                                    <span
                                        className={cn(
                                            "px-2 rounded-md h-7 inline-flex items-center justify-center",
                                            {
                                                "text-[#00563E] bg-[rgba(204,250,233,0.50)] hover:bg-[rgba(204,250,233,0.50)] ":
                                                    fee.alt_email_sent,
                                                "text-[#981F41]  bg-[rgba(251,199,198,0.50)] hover:bg-[rgba(251,199,198,0.50)] ":
                                                    !fee.alt_email_sent,
                                            }
                                        )}
                                    >
                                        {fee.alt_email_sent ? "Yes" : "No"}
                                    </span>
                                )}
                            </div>
                        ) : null}
                        <span className="col-span-6 text-caption whitespace-pre-wrap">
                            {fee.reason_for_update || "--"}
                        </span>
                    </div>
                </div>
            </div>
        </Card>
    );
}
